import { useState } from 'react'
import { authClient } from '@papan/auth/client'

interface GoogleButtonProps {
  label: string
  callbackURL?: string
  tabIndex?: number
}

// Google "G" logo as inline SVG
function GoogleLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4" />
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853" />
      <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05" />
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335" />
    </svg>
  )
}

export function GoogleButton({ label, callbackURL, tabIndex }: GoogleButtonProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const handleClick = async () => {
    setError('')
    setIsLoading(true)
    try {
      await authClient.signIn.social({
        provider: 'google',
        callbackURL: callbackURL || `${window.location.origin}/~/folder`,
        // WITHOUT THIS, A REFUSED GOOGLE SIGN-IN DEAD-ENDS ON A 404.
        //
        // Better Auth's social callback reports failure through
        // `redirectOnError`, whose destination is the `errorURL` carried in the
        // OAuth state — i.e. exactly this value. Unset, it falls back to
        // `${baseURL}/error` (measured: `GET /api/auth/error?error=UNKNOWN` →
        // 200, better-auth's own error page) — NOT a 404 at the API root, which
        // is what an earlier version of this comment claimed. The user still
        // ends up on a generic page on the API origin, away from the app, and
        // the message is still collapsed to `UNKNOWN` by
        // `/^['A-Za-z0-9_-]+$/` because our sentence ends in a full stop. So the
        // decision stands; only the stated consequence was wrong.
        //
        // Reachable since the #351 guard: signing in with Google using an
        // address that is a verified secondary on another account is refused in
        // `user.create.before`, and that refusal travels this path. Pointing it
        // back at /login means the message survives whole and lands somewhere a
        // person can act.
        errorCallbackURL: `${window.location.origin}${loginAgainPath()}`,
      })
    } catch {
      setError('Google sign-in is not available right now.')
      setIsLoading(false)
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={isLoading}
        tabIndex={tabIndex}
        className="w-full py-2.5 px-4 bg-white hover:bg-gray-50 disabled:opacity-60 text-gray-700 rounded-lg font-medium text-sm border border-gray-300 flex items-center justify-center gap-3 transition-colors"
      >
        <GoogleLogo />
        {isLoading ? '...' : label}
      </button>
      {error && (
        <p className="mt-2 text-xs text-red-400 text-center">{error}</p>
      )}
    </>
  )
}

/** Back to /login after a refused Google sign-in — keeping where the person
 *  was headed (`next`; the login page re-checks it with `safeNext`). */
function loginAgainPath(): string {
  const next = new URLSearchParams(window.location.search).get('next')
  return next ? `/login?next=${encodeURIComponent(next)}` : '/login'
}
